import { ChatMessageGroup } from '@provision/chat-utils';
import dayjs from 'dayjs';
import { useContext, useMemo } from 'react';
import type { ConversationParticipant } from 'src/graphql/entities/conversation/conversation.types';
import { useNewMessagesAnchor } from 'src/pages/Conversations/Chat/new-messages-anchor-context';
import { partitionGroupMessagesByTimeWindows } from 'src/pages/Conversations/Chat/partition-group-messages-by-time-window';
import { useTimelineBoundaryTimestamps } from 'src/pages/Conversations/Chat/registry/hooks';
import {
  ChildStatus,
  MessageTypeId,
  MessageTypeRegistration,
  TimelineEntry,
} from 'src/pages/Conversations/Chat/registry/types';
import {
  DEFAULT_TIMEOUT_MS,
  useReadinessWithTimeout,
} from 'src/pages/Conversations/Chat/registry/use-readiness-with-timeout';
import { useSmsSessionContext } from 'src/pages/Conversations/Chat/SmsSession/sms-session-context';
import { getSuppressedMessageIds } from 'src/pages/Conversations/Chat/SmsSession/sms-session-grouping';
import { ChatMessageContext, InitialPhase } from './chat-message-context';
import { ChatMessageProvider } from './chat-message-provider';
import { ChatMessageRow } from './chat-message-row';

function useItems(): TimelineEntry<ChatMessageGroup<ConversationParticipant>>[] {
  const { messagesWithMetadata, initialPhase } = useContext(ChatMessageContext);
  const boundaryTimestamps = useTimelineBoundaryTimestamps();
  const { anchor: newMessagesAnchor } = useNewMessagesAnchor();

  /*
    Splitting on the millisecond after the anchor keeps the anchor message on the
    already-seen side, so the boundary lands between groups rather than swallowing it.
  */
  const partitionTimestamps = useMemo(() => {
    if (!newMessagesAnchor) return boundaryTimestamps;

    return [...boundaryTimestamps, dayjs(newMessagesAnchor).add(1, 'millisecond')].toSorted(
      (a, b) => a.valueOf() - b.valueOf(),
    );
  }, [boundaryTimestamps, newMessagesAnchor]);

  const sessionGroups = useSmsSessionContext((v) => v.sessionGroups);
  const suppressedIds = useMemo(() => getSuppressedMessageIds(sessionGroups), [sessionGroups]);

  return useMemo(() => {
    if (initialPhase !== InitialPhase.Ready) return [];

    return messagesWithMetadata.flatMap((group) =>
      partitionGroupMessagesByTimeWindows<ConversationParticipant>(group, partitionTimestamps)
        .map((partitioned) => dropSessionMessages(partitioned, suppressedIds))
        .filter((partitioned) => partitioned !== null)
        .map((partitioned) => ({
          type: MessageTypeId.ChatMessage,
          timestamp: partitioned.createdAt,
          item: partitioned,
        })),
    );
  }, [messagesWithMetadata, initialPhase, partitionTimestamps, suppressedIds]);
}

function dropSessionMessages(
  group: ChatMessageGroup<ConversationParticipant>,
  suppressedIds: Set<string>,
): ChatMessageGroup<ConversationParticipant> | null {
  if (suppressedIds.size === 0) return group;

  const remaining = group.messages.filter((m) => !suppressedIds.has(m.id));
  if (remaining.length === group.messages.length) return group;
  if (remaining.length === 0) return null;

  return {
    ...group,
    id: remaining[0].id,
    createdAt: remaining[0].createdAt,
    messages: remaining,
  };
}

function useReadiness(): ChildStatus {
  const { isConversationLoading, initialPhase, messagesWithMetadata } =
    useContext(ChatMessageContext);

  // useConversation does not expose an `error` field, so query failures surface
  // as Loading → Timeout after DEFAULT_TIMEOUT_MS rather than entering
  // ChildStatus.Error immediately.
  return useReadinessWithTimeout({
    loading: initialPhase !== InitialPhase.Ready || isConversationLoading,
    hasData: messagesWithMetadata.length > 0,
    timeoutMs: DEFAULT_TIMEOUT_MS,
  });
}

function useLoadMore(): (() => Promise<void>) | undefined {
  const { loadMore } = useContext(ChatMessageContext);
  return loadMore;
}

function usePaginationState(): { hasMore: boolean; isLoadingMore: boolean } {
  const { hasMore, isLoadingMore } = useContext(ChatMessageContext);
  return { hasMore, isLoadingMore };
}

export const chatMessageRegistration: MessageTypeRegistration<
  ChatMessageGroup<ConversationParticipant>
> = {
  id: MessageTypeId.ChatMessage,
  Row: ChatMessageRow,
  Provider: ChatMessageProvider,
  /*
    item.id is the group's NEWEST message id (see groupMessagesByParticipantIdentity),
    which changes on every send (and again when an optimistic id is swapped for the
    server id) — remounting the whole group's rows each time. messages is stored
    oldest-first, so the oldest message's id stays put while newer ones are appended,
    keeping the row's reconciliation identity stable across sends.
  */
  getKey: (item) => item.messages[0]?.id ?? item.id,
  useItems,
  useReadiness,
  useLoadMore,
  usePaginationState,
  blockingForInitialRender: true,
  timeoutMs: DEFAULT_TIMEOUT_MS,
};

export default chatMessageRegistration;
